import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import classNames from 'classnames';
import { Button } from 'react-bootstrap-5';

import ClearFilters from 'components/search_page/clear_filters';

import './styles.scss';

// Wraps children onto extra lines, hiding them by default and rendering a toggle button when necessary
// The only filter-specific things are the toggle copy and these two assumptions, used for width calcs:
//   * All children use a consistent margin-right to space themselves (except for the last one)
//   * All children have a 1px border
// TODO: Consider getting the "more filters" button to not be right aligned by:
//   * Making the toggle's container empty but with a fixed width of the size of the toggle
//     * Need to do this to ensure the DOM can still wrap things at the right point
//     * And of course, still only render it when something is wrapping
//   * In the resize observer callback, find the last unwrapped filter
//   * Position the more filters button absolutely just to the right of the last unwrapped filter
export default function ResponsiveFilterContainer({ className, children, onClearFilters }) {
  const [showAll, setShowAll] = useState(false);
  const [showToggleControl, setShowToggleControl] = useState(false);

  const containerRef = useRef();
  const childContainerRef = useRef();
  const checkScheduledRef = useRef(false);

  const checkOverflow = useCallback(() => {
    checkScheduledRef.current = false;

    const childContainer = childContainerRef.current;
    if (!childContainer) return;

    const firstChild = childContainer.firstElementChild;
    const lastChild = childContainer.lastElementChild;
    if (!firstChild || !lastChild) return;

    const hasOverflow = firstChild.offsetTop !== lastChild.offsetTop;
    setShowToggleControl((prev) => {
      if (prev !== hasOverflow) {
        return hasOverflow;
      }
      return prev;
    });

  }, []);

  const scheduleCheck = useCallback(() => {
    if (!checkScheduledRef.current) {
      checkScheduledRef.current = true;
      requestAnimationFrame(checkOverflow);
    }
  }, [checkOverflow]);

  useEffect(() => {
    const container = containerRef.current;
    const childContainer = childContainerRef.current;
    if (!container || !childContainer) return;

    const resizeObserver = new ResizeObserver(scheduleCheck);
    resizeObserver.observe(container);
    resizeObserver.observe(childContainer);

    const mutationObserver = new MutationObserver(scheduleCheck);
    mutationObserver.observe(childContainer, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class']
    });

    scheduleCheck();

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };

  }, [scheduleCheck]);


  return (
    <div
      className={classNames('responsive-filter-container', className, { 'show-all': showAll })}
      ref={containerRef}
    >
      <div className="d-flex">
        <div className="responsive-filters gap-2" ref={childContainerRef}>
          {children}
        </div>
        {onClearFilters &&
          <div>
            <ClearFilters onClick={onClearFilters} />
          </div>
        }
      </div>

      {showToggleControl &&
        <Button
          className="responsive-filters-toggle text-nowrap"
          variant="outline"
          onClick={() => setShowAll(!showAll)}
        >
          {showAll ? 'Less filters' : 'More filters'}
        </Button>
      }
    </div>
  );
}

ResponsiveFilterContainer.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  onClearFilters: PropTypes.func
};
